import React, { useState } from 'react';
import { ArrowUpRight, Play } from 'lucide-react';
import { WORK_VIDEOS, YOUTUBE_CHANNEL, FEATURED_VIDEO_ID } from '@/lib/content';
import SectionHeading from '@/components/sections/SectionHeading';
import SEO from '@/components/SEO';

export default function Work() {
    const [activeId, setActiveId] = useState(FEATURED_VIDEO_ID);

    return (
        <div className="pt-28 bg-white">
            <SEO title="Our Work" description="Direct response TV spots, infomercials, CTV/OTT and social cut-downs produced by CFL Ad Group." path="/work" />

            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-12 md:pb-16">
                <div className="text-xs uppercase tracking-[0.35em] font-bold text-brand-orange mb-5">
                    The Reel
                </div>
                <h1 className="font-display font-black text-brand-navy text-4xl sm:text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] max-w-5xl">
                    Work that runs<br />
                    <span className="italic font-medium text-brand-orange">on air, all year.</span>
                </h1>
                <p className="text-brand-navy/70 text-base md:text-lg max-w-2xl mt-6 md:mt-8 leading-relaxed">
                    Long-form infomercials, 30-second TV spots, and social cut-downs — produced in-house
                    and trafficked across broadcast, cable, CTV/OTT, and digital platforms.
                </p>
            </section>

            {/* Main player */}
            <section className="bg-brand-navy">
                <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-10 md:py-16">
                    <div className="relative aspect-video bg-black overflow-hidden">
                        <iframe
                            key={activeId}
                            title={`CFL Ad Group — ${activeId}`}
                            src={`https://www.youtube.com/embed/${activeId}?rel=0&modestbranding=1&autoplay=0`}
                            className="absolute inset-0 w-full h-full"
                            frameBorder="0"
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                </div>
            </section>

            {/* Grid of videos */}
            <section className="bg-brand-cream py-12 md:py-16 lg:py-20">
                <div className="max-w-[1400px] mx-auto px-4 md:px-10">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-12">
                        <SectionHeading eyebrow="Selected Spots" title="A taste of the catalog." />
                        <a
                            href={YOUTUBE_CHANNEL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="work-youtube-channel-link"
                            className="inline-flex items-center gap-2 text-brand-navy hover:text-brand-orange text-xs uppercase tracking-[0.25em] font-bold self-start"
                        >
                            Visit YouTube channel <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                        {WORK_VIDEOS.map((v, idx) => {
                            const isActive = activeId === v.id;
                            return (
                                <button
                                    type="button"
                                    key={`${v.id}-${idx}`}
                                    onClick={() => {
                                        setActiveId(v.id);
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    data-testid={`work-video-${idx}`}
                                    aria-label={`Play video ${v.title}`}
                                    className={`group text-left bg-white border ${isActive ? 'border-brand-orange shadow-[0_20px_50px_-20px_rgba(31,27,77,0.25)]' : 'border-brand-navy/10'} hover:border-brand-orange hover:shadow-[0_20px_50px_-20px_rgba(31,27,77,0.2)] transition-all`}
                                >
                                    <div className="relative aspect-video bg-brand-navy overflow-hidden">
                                        <img
                                            src={`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`}
                                            alt={v.title}
                                            loading="lazy"
                                            className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange text-white shadow-lg group-hover:scale-110 transition-transform">
                                                <Play className="w-5 h-5 fill-white" />
                                            </span>
                                        </div>
                                        {isActive && (
                                            <div className="absolute top-2 left-2 bg-brand-orange text-white text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1">
                                                Now Playing
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4 md:p-5">
                                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-brand-orange mb-2 font-bold">
                                            Reel · {String(idx + 1).padStart(2, '0')}
                                        </div>
                                        <h3 className="font-display font-extrabold text-base md:text-lg text-brand-navy leading-tight line-clamp-2">
                                            {v.title}
                                        </h3>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-12 md:mt-16">
                        <a
                            href={YOUTUBE_CHANNEL}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-3 bg-brand-orange hover:bg-brand-navy text-white px-7 md:px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] transition-colors"
                        >
                            <Play className="w-4 h-4" /> Watch more on YouTube
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
